'use client'

import * as Sentry from '@sentry/react'
import React, { createContext, useCallback, useContext, useRef, useState } from 'react'
import { toast } from 'sonner'
import type { CheckpointResponseResponse } from '@/api/client'
import { CheckpointDialog } from '@/components/organisms/checkpoint-dialog/checkpoint-dialog'
import i18n from '@/i18n'
import type { Checkpoint } from '@/utils/checkpoint.utils'

/** A single checkpoint the provider should display. Carries everything the
 * dialog needs plus the callbacks for how the requester wants to react. */
export type CheckpointRequest = {
  workOrderId: string
  workflowStateId: string
  workshopId: string
  workOrderType?: string | null
  checkpoint: Checkpoint
  currentUserId: string
  registrationNumber?: string
  /** When set, the dialog reopens this submission pre-filled and PATCHes it
   * instead of creating a new one (before-enter-exit's exit phase). */
  existingResponse?: CheckpointResponseResponse
  /** Run after the responses are saved. Used by blocking gates to proceed. */
  onComplete?(): void
  /** Run if the dialog is dismissed without completing. Used to abort a gated action. */
  onCancel?(): void
}

type CheckpointContextValue = {
  requestCheckpoint(request: CheckpointRequest): void
}

/**
 * Fail-closed fallback used when a consumer somehow resolves this context
 * without the provider above it. That should be impossible by tree shape —
 * CheckpointProvider is mounted unconditionally in `providers/index.tsx`, above
 * the router Outlet — but it does happen when two instances of this module are
 * live at once and the consumer holds a different context object than the
 * provider: a Vite HMR partial update in dev, or a freshly-hashed lazy route
 * chunk mounting into a tab that has been open across a deploy.
 *
 * It must never call the requester's `onComplete`: checkpoints gate work-order
 * moves, so proceeding would silently bypass a mandatory step. Swallowing the
 * request aborts the move and leaves the board untouched, which is recoverable;
 * throwing here took down the whole app through the router's error boundary.
 */
const fallbackValue: CheckpointContextValue = {
  requestCheckpoint: request => {
    const error = new Error(
      'requestCheckpoint called without a CheckpointProvider — likely a stale duplicate of checkpoint-context (HMR update or post-deploy chunk). The checkpoint was not shown and the action was aborted.',
    )
    console.error(error, request)
    Sentry.captureException(error, {
      extra: { workOrderId: request.workOrderId, checkpointId: request.checkpoint.id },
    })
    toast.error(i18n.t('checkpointDialog.providerUnavailable'))
  },
}

const CheckpointContext = createContext<CheckpointContextValue>(fallbackValue)

type QueuedRequest = CheckpointRequest & { id: number }

/**
 * Owns a queue of checkpoint requests and renders a single, persistent
 * CheckpointDialog at the app root. Because the dialog lives here — above the
 * kanban cards — it survives a card unmounting (e.g. after a move re-buckets
 * the card out of its column). All move checkpoints now gate the move (they
 * resolve before it commits), but hosting the dialog here keeps it decoupled
 * from the card and lets several gated dialogs queue and run in sequence.
 */
export const CheckpointProvider = ({ children }: { children: React.ReactNode }) => {
  const [queue, setQueue] = useState<QueuedRequest[]>([])
  const nextIdRef = useRef(0)

  const requestCheckpoint = useCallback((request: CheckpointRequest) => {
    const id = nextIdRef.current++
    setQueue(prev => [...prev, { ...request, id }])
  }, [])

  const dequeue = useCallback(() => {
    setQueue(prev => prev.slice(1))
  }, [])

  const current = queue[0]

  return (
    <CheckpointContext.Provider value={{ requestCheckpoint }}>
      {children}
      {current && (
        <CheckpointDialog
          // Force a fresh instance per request: CheckpointDialog only resets its
          // answers when `open` toggles, so swapping checkpoints while staying
          // open would otherwise carry answers across.
          key={current.id}
          open
          checkpoint={current.checkpoint}
          workOrderId={current.workOrderId}
          workflowStateId={current.workflowStateId}
          workshopId={current.workshopId}
          workOrderType={current.workOrderType}
          currentUserId={current.currentUserId}
          registrationNumber={current.registrationNumber}
          existingResponse={current.existingResponse}
          onComplete={() => {
            current.onComplete?.()
            dequeue()
          }}
          onOpenChange={open => {
            if (!open) {
              current.onCancel?.()
              dequeue()
            }
          }}
        />
      )}
    </CheckpointContext.Provider>
  )
}

export function useCheckpoint() {
  return useContext(CheckpointContext)
}

// This module is not a Fast Refresh boundary (it exports the `useCheckpoint`
// hook alongside the component), so an edit propagates to importers and can
// leave a re-evaluated consumer holding a newer CheckpointContext than the
// still-mounted provider. Force a full reload instead so identity stays in sync.
if (import.meta.hot) {
  import.meta.hot.accept(() => window.location.reload())
}
